import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CapabilitiesService, PartnerMembershipService } from '@sisoma/auth';
import { AppShell, BackToApp, Sidenav, ThemeToggle } from '@sisoma/ui';

import { environment } from '../../../environments/environment';
import { FOOTER_NAV_ITEMS, NAV_SECTIONS, filterNav, filterNavLinks } from '../navigation/nav-items';
import { MembershipGate } from '../../features/membership/membership-gate';
import { PartnerSwitcher } from './partner-switcher/partner-switcher';

/**
 * The partner app's composition of the shared shell: the frame and sidenav
 * behaviour come from `@sisoma/ui`, this component supplies what is specific
 * to this app — the nav registry, the brand, and the sidenav footer content.
 *
 * The nav is filtered through `CapabilitiesService` so members only see the
 * entries their active-partner capabilities permit. `has()` reads the
 * `capabilities()` signal, so both computeds re-filter reactively when the
 * capability set or active partner changes.
 */
@Component({
  selector: 'app-shell',
  imports: [MembershipGate, AppShell, Sidenav, ThemeToggle, PartnerSwitcher, BackToApp],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Shell {
  private readonly caps = inject(CapabilitiesService);
  private readonly membership = inject(PartnerMembershipService);

  /**
   * Nav is blanked while a whole-page membership message is showing: a person
   * being told they have no role should not be looking at a half-filled
   * sidebar, and every entry it could still offer is one they cannot use.
   *
   * Gated on `blocked()` alone, deliberately not on `resolving()` too —
   * emptying the nav for the length of the membership request would make every
   * normal user's sidebar pop in on load, which is a far more visible cost than
   * the one link a zero-capability user briefly keeps.
   *
   * The `uiSidenavFooter` slot renders regardless, so the brand, the partner
   * switcher, the theme toggle and "Back to Halio" all survive a blank nav.
   * That is what stops a multi-partner user being stranded in the one area
   * they have no role in.
   */
  protected readonly sections = computed(() =>
    this.membership.blocked() ? [] : filterNav(NAV_SECTIONS, (c) => this.caps.has(c)),
  );
  protected readonly footerLinks = computed(() =>
    this.membership.blocked() ? [] : filterNavLinks(FOOTER_NAV_ITEMS, (c) => this.caps.has(c)),
  );

  /** Root of the legacy `users` app for the "Back to Halio" footer link. */
  protected readonly usersAppUrl = environment.usersAppUrl;
}
